import React, { useEffect, useRef, useState } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import ReactMarkdown from "react-markdown"
import { Message } from "../km"
import { MessageActions } from "./ui/MessageActions"
import { scrollbarMixin } from "src/providers/AppProviders"
import { useResponsive } from "src/utils/useResponsive"
import { BotIcon } from "./BotIcon"
import LoadingDots from "./ui/LoadingDots"
import ThumbsPopup from "./ui/ThumbsPopup"
import axios from "axios"
import { SourcesDrawer } from "./SourcesDrawer"
import ExfluencyLoader from "src/components/ExfluencyLoader"

interface KmMessageListProps {
  messages: Message[]
  botIcon: string
  showSources: boolean
  loading: boolean
  companyId: string
  chatbot: boolean
  query?: string
  isInputFocused?: boolean
  companyName?: string
  selectedTaxonomyCode?: string
}

export const KmMessageList: React.FC<KmMessageListProps> = ({
  messages,
  botIcon,
  showSources,
  loading,
  companyId,
  chatbot,
  query,
  isInputFocused,
  companyName,
  selectedTaxonomyCode
}) => {
  const { isMobile } = useResponsive()
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [commentMessage, setCommentMessage] = useState<Message | null>(null)

  const [selectedMessage, setSelectedMessage] = useState<Message>()
  const [selectedQuestion, setSelectedQuestion] = useState<string>()

  const noQuestionsYet = messages.length < 2
  const showBotIcon = !isMobile || chatbot

  const messageListRef = useRef<HTMLDivElement>(null)

  //scroll to bottom of chat
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }, [messages])

  const handleCommentSubmit = (comment: string, message: Message | null) => {
    setCommentMessage(null)
    setIsPopupOpen(false)
    if (!message) return
    message.comment = comment
    saveComment(message)
  }
  const handleCommentClose = (message: Message | null) => {
    setCommentMessage(null)
    setIsPopupOpen(false)
    if (!message) return
    saveComment(message)
  }
  async function saveComment(message: Message) {
    if (!message.id) return
    if (!message.comment && message.thumbUp == undefined) return
    const body = {
      id: message.id,
      comment: message.comment,
      score: message.thumbUp == undefined ? null : message.thumbUp ? 1 : -1,
      company_id: companyId
    }
    await axios.post(`/api/km/comment`, body)
  }
  function handleThumb(up: boolean | undefined, message: Message): void {
    if (up != undefined) {
      message.thumbUp = up
    }
    setCommentMessage(message)
    setIsPopupOpen(true)
  }

  // Create a wrapper function that matches the MessageActions expected signature
  const handleThumbWrapper = (up: boolean | undefined, message: Message, index: number) => {
    handleThumb(up, message)
  }

  const openSourcesDrawer = (message: Message) => {
    const questionIndex = messages.findIndex((m) => m.id == message.id) - 1
    const question = messages[questionIndex].message
    setSelectedMessage(message)
    setSelectedQuestion(question)
  }

  return (
    <Box
      ref={messageListRef}
      sx={{
        px: { xs: 1, md: 2 },
        scrollbarGutter: "stable",
        overflowY: "auto",
        flex: 1,
        minHeight: 0,
        overflowWrap: "anywhere",
        ...scrollbarMixin()
      }}
    >
      <>
        {selectedMessage && selectedQuestion && (
          <SourcesDrawer 
            message={selectedMessage} 
            setMessage={setSelectedMessage} 
            question={selectedQuestion}
            selectedTaxonomyCode={selectedTaxonomyCode}
          />
        )}
        <ThumbsPopup
          isOpen={isPopupOpen}
          onRequestClose={handleCommentClose}
          onSubmitComment={handleCommentSubmit}
          message={commentMessage}
        />
        {messages.map((message, index) => {
          const msg =
            message.type === "apiMessage" ? (
              <>
                <Box sx={{ display: "flex", p: { xs: 1, md: 2 } }}>
                  {showBotIcon && (
                    <Box sx={{ flexShrink: 0 }}>
                      <BotIcon botIcon={botIcon} size={noQuestionsYet ? "medium" : "small"} />
                    </Box>
                  )}
                  <BotMessage
                    message={message.message}
                    makeFirstMessageBigger={!chatbot && noQuestionsYet && !isInputFocused}
                    index={index}
                    chatbot={chatbot}
                  />
                </Box>
              </>
            ) : (
              <UserMessage message={message.message} index={index} />
            )

          const messageActions =
            message.type === "apiMessage" && index != 0 && message && showSources ? (
              <MessageActions
                index={index}
                myMsg={message as Message}
                handleThumb={handleThumbWrapper}
                onSourcesClick={() => openSourcesDrawer(message)}
                messageId={`km-message-${index}`}
              />
            ) : undefined

          return (
            <Box key={`chatMessage-${index}`}>
              {msg}
              {messageActions}
            </Box>
          )
        })}
        {loading && chatbot && (
          <Box sx={{ display: "flex", p: { xs: 1, md: 2 } }}>
            {showBotIcon && (
              <Box sx={{ flexShrink: 0 }}>
                <BotIcon botIcon={botIcon} size={"small"} />
              </Box>
            )}
            <Box
              sx={{
                backgroundColor: "#F9F9F9",
                borderRadius: "0px 15px 15px 15px",
                px: { xs: 2 },
                py: { xs: 2 },
                alignItems: "center",
                marginTop: "20px"
              }}
            >
              <LoadingDots />
            </Box>
          </Box>
        )}
        {loading && !chatbot && (
          <Box sx={{ display: "flex", p: { xs: 1, md: 2 } }}>
            <Box
              sx={{
                alignItems: "center",
                marginTop: "20px"
              }}
            >
              <ExfluencyLoader noBackgroundColor sx={{ width: "70px", height: "70px" }} />
            </Box>
          </Box>
        )}
      </>
    </Box>
  )
}

export const UserMessage = ({ message, index }: { message: string; index: number }) => {
  const { palette } = useTheme()
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Box
        sx={{
          backgroundColor: palette.primary.light,
          py: { xs: 1, md: 2 },
          pl: { xs: 2, md: 3 },
          pr: { xs: 4, md: 6 },
          maxWidth: { xs: "85%", md: "70%" },
          borderRadius: "15px 0px 15px 15px"
        }}
      >
        <Typography variant="body1" dir="auto" sx={{ fontSize: { xs: "15px", md: "16px" } }}>
          {message}
        </Typography>
      </Box>
    </Box>
  )
}

export const BotMessage = ({
  message,
  makeFirstMessageBigger,
  index,
  chatbot
}: {
  message: string
  makeFirstMessageBigger: boolean
  index: number
  chatbot: boolean
}) => {
  const { palette, typography } = useTheme()

  return (
    <Box
      sx={[
        makeFirstMessageBigger
          ? {
              fontWeight: 500
            }
          : {},
        {
          color: palette.text.primary,
          marginTop: { xs: 0, md: "20px" }
        }
      ]}
    >
      <Box
        dir="auto"
        id={`km-message-${index}`}
        sx={{
          backgroundColor: chatbot ? { xs: "#F9F9F9" } : { xs: "#white", md: "#F9F9F9" },
          borderRadius: "0px 15px 15px 15px",
          px: chatbot ? { xs: 2 } : { xs: 0, md: 2 },
          py: { xs: 0.1 },
          fontFamily: typography.fontFamily,
          "& p": makeFirstMessageBigger
            ? { fontSize: { xs: "38px" }, fontWeight: 600 }
            : { fontSize: { xs: "15px", md: "16px" }, ...(index === 0 ? { fontWeight: 600 } : {}) },
          "& ol, & ul": {
            paddingLeft: { xs: "16px", md: "24px" }
          },
          "& li": {
            marginLeft: { xs: "8px", md: "16px" }
          }
        }}
      >
        <ReactMarkdown linkTarget="_blank">{message}</ReactMarkdown>
      </Box>
    </Box>
  )
}